import React, { useRef, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';
import {
  Download,
  Loader2,
  X,
  Zap,
  Heart,
  Droplets,
  Fish,
  Moon,
  Brain,
  Flame,
  Dumbbell,
  Sparkles,
  Trophy,
  Megaphone,
  Target,
  Users,
  Monitor,
} from 'lucide-react';

// Bright, distinct hues — used as colored step accents (à la Venngage)
const STEP_HUES = [
  { name: 'orange', solid: '#FF5A1F', soft: '#FFE4D6', text: '#FF5A1F' },
  { name: 'green',  solid: '#22A06B', soft: '#D4F1E2', text: '#22A06B' },
  { name: 'blue',   solid: '#2D7BF4', soft: '#D6E4FB', text: '#2D7BF4' },
  { name: 'amber',  solid: '#E0A310', soft: '#FFF1D1', text: '#E0A310' },
  { name: 'red',    solid: '#E23B3B', soft: '#FBE0E0', text: '#E23B3B' },
  { name: 'violet', solid: '#7C3AED', soft: '#EADFFF', text: '#7C3AED' },
];

const ICONS = {
  muscle: Dumbbell,
  recovery: Zap,
  hydration: Droplets,
  fish: Fish,
  sleep: Moon,
  brain: Brain,
  energy: Flame,
  fire: Flame,
  default: Heart,
};

const POPPINS = "'Poppins', 'Inter', system-ui, sans-serif";

const formatINR = (n) => {
  if (n == null || isNaN(n)) return null;
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(2)} Cr`;
  if (n >= 100_000) return `₹${(n / 100_000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

const hasMetrics = (m) =>
  m && (m.roas != null || m.sales != null || m.ctr != null || m.delivery_rate != null);

const hasLandingWinner = (lw) => {
  if (!lw) return false;
  const lbl = (lw.label || '').toLowerCase();
  return lbl && !lbl.includes('not mentioned') && lbl !== '—' && lbl !== '-';
};

// Numbered circle badge — the signature element of the reference style
const NumberBadge = ({ n, hue }) => (
  <div
    className="relative flex items-center justify-center rounded-full"
    style={{
      width: 56,
      height: 56,
      backgroundColor: hue.solid,
      boxShadow: `0 6px 18px -6px ${hue.solid}80`,
    }}
  >
    <span
      style={{
        color: '#FFFFFF',
        fontFamily: POPPINS,
        fontWeight: 800,
        fontSize: 26,
        lineHeight: 1,
      }}
    >
      {n}
    </span>
  </div>
);

// One brand = one "step" in the horizontal flow
const BrandStep = ({ col, idx }) => {
  const hue = STEP_HUES[idx % STEP_HUES.length];
  const Icon = ICONS[col?.theme?.icon] || ICONS.default;
  const m = col?.metrics || {};
  const cohorts = (col?.cohorts || []).filter(Boolean);
  const scale = (col?.what_to_scale || []).filter(Boolean);
  const showMetrics = hasMetrics(m);
  const showTheme = col?.theme?.title && !/^(no learnings|—|-)/i.test(col.theme.title);
  const showLanding = hasLandingWinner(col?.landing_winner);

  return (
    <div
      className="relative flex flex-col"
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: 18,
        padding: '28px 26px 26px',
        border: `2px solid ${hue.solid}`,
        boxShadow: `0 18px 36px -18px ${hue.solid}55, 0 4px 10px -4px ${hue.solid}25`,
      }}
    >
      {/* Top accent ribbon */}
      <div
        style={{
          position: 'absolute',
          top: -2,
          left: -2,
          right: -2,
          height: 8,
          backgroundColor: hue.solid,
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
        }}
      />
      {/* Spacer so content clears the ribbon */}
      <div style={{ height: 6 }} />
      {/* Header row: badge + brand name + count */}
      <div className="flex items-center gap-3.5" style={{ marginBottom: 18 }}>
        <NumberBadge n={idx + 1} hue={hue} />
        <div className="min-w-0 flex-1">
          <div
            style={{
              fontFamily: POPPINS,
              fontWeight: 800,
              fontSize: 26,
              lineHeight: 1.05,
              color: hue.text,
              letterSpacing: '-0.01em',
              textTransform: 'uppercase',
            }}
          >
            {col?.brand}
          </div>
          <div
            style={{
              fontFamily: POPPINS,
              fontWeight: 600,
              fontSize: 12,
              color: '#9CA3AF',
              marginTop: 6,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {col?.learning_count ?? 0} learning{(col?.learning_count ?? 0) === 1 ? '' : 's'}
            {col?.creatives_count != null && (
              <> · {col.creatives_count} creative{col.creatives_count === 1 ? '' : 's'}</>
            )}
          </div>
        </div>
      </div>

      {/* Optional campaign label */}
      {col?.campaign && col.campaign !== '—' && (
        <div
          style={{
            fontFamily: POPPINS,
            fontWeight: 700,
            fontSize: 17,
            color: '#111827',
            marginBottom: 14,
            lineHeight: 1.3,
          }}
        >
          {col.campaign}
        </div>
      )}

      {/* Metrics inline */}
      {showMetrics && (
        <div className="flex flex-wrap gap-2" style={{ marginBottom: 20 }}>
          {m.roas != null && (
            <span
              className="rounded-md"
              style={{
                fontFamily: POPPINS,
                fontWeight: 700,
                fontSize: 13,
                color: hue.text,
                backgroundColor: hue.soft,
                padding: '6px 12px',
              }}
            >
              ROAS <span className="tabular-nums" style={{ marginLeft: 4 }}>{Number(m.roas).toFixed(2)}</span>
            </span>
          )}
          {m.sales != null && (
            <span
              className="rounded-md"
              style={{
                fontFamily: POPPINS,
                fontWeight: 700,
                fontSize: 13,
                color: '#1F2937',
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                padding: '6px 12px',
              }}
            >
              SALES <span className="tabular-nums" style={{ marginLeft: 4 }}>{formatINR(m.sales)}</span>
            </span>
          )}
          {m.ctr != null && (
            <span
              className="rounded-md"
              style={{
                fontFamily: POPPINS,
                fontWeight: 700,
                fontSize: 13,
                color: '#1F2937',
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                padding: '6px 12px',
              }}
            >
              CTR <span className="tabular-nums" style={{ marginLeft: 4 }}>{Number(m.ctr).toFixed(2)}%</span>
            </span>
          )}
          {m.delivery_rate != null && (
            <span
              className="rounded-md"
              style={{
                fontFamily: POPPINS,
                fontWeight: 700,
                fontSize: 13,
                color: '#1F2937',
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                padding: '6px 12px',
              }}
            >
              DELIVERY <span className="tabular-nums" style={{ marginLeft: 4 }}>{Number(m.delivery_rate).toFixed(2)}%</span>
            </span>
          )}
        </div>
      )}

      {/* Theme */}
      {showTheme && (
        <div className="flex items-start gap-3" style={{ marginBottom: 20 }}>
          <div
            className="shrink-0 rounded-lg flex items-center justify-center"
            style={{ width: 34, height: 34, backgroundColor: hue.soft }}
          >
            <Icon style={{ width: 16, height: 16, color: hue.solid }} strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div
              style={{
                fontFamily: POPPINS,
                fontWeight: 800,
                fontSize: 15,
                color: hue.text,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                lineHeight: 1.25,
              }}
            >
              {col.theme.title}
            </div>
            {col.theme.subtitle && (
              <div
                style={{
                  fontFamily: POPPINS,
                  fontWeight: 400,
                  fontSize: 14.5,
                  color: '#4B5563',
                  lineHeight: 1.55,
                  marginTop: 6,
                }}
              >
                {col.theme.subtitle}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cohorts */}
      {cohorts.length > 0 && (
        <div className="w-full" style={{ marginBottom: 20 }}>
          <div
            style={{
              fontFamily: POPPINS,
              fontWeight: 700,
              fontSize: 11.5,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#9CA3AF',
              marginBottom: 10,
            }}
          >
            Cohorts
          </div>
          <ul className="space-y-2.5">
            {cohorts.slice(0, 4).map((c, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span
                  className="shrink-0 rounded-full"
                  style={{ width: 7, height: 7, backgroundColor: hue.solid, marginTop: 8 }}
                />
                <span
                  style={{
                    fontFamily: POPPINS,
                    fontWeight: 400,
                    fontSize: 14.5,
                    color: '#374151',
                    lineHeight: 1.5,
                  }}
                >
                  {c}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Landing winner */}
      {showLanding && (
        <div className="w-full" style={{ marginBottom: 20 }}>
          <div
            style={{
              fontFamily: POPPINS,
              fontWeight: 800,
              fontSize: 14,
              color: hue.text,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              lineHeight: 1.3,
            }}
          >
            {col.landing_winner.label}
          </div>
          {col.landing_winner.blurb && (
            <div
              style={{
                fontFamily: POPPINS,
                fontWeight: 400,
                fontSize: 14.5,
                color: '#4B5563',
                lineHeight: 1.55,
                marginTop: 6,
              }}
            >
              {col.landing_winner.blurb}
            </div>
          )}
        </div>
      )}

      {/* What to scale */}
      {scale.length > 0 && (
        <div className="w-full">
          <div
            style={{
              fontFamily: POPPINS,
              fontWeight: 700,
              fontSize: 11.5,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#9CA3AF',
              marginBottom: 12,
            }}
          >
            What to scale
          </div>
          <ul className="space-y-2.5">
            {scale.slice(0, 4).map((c, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span
                  className="shrink-0 rounded-[5px] flex items-center justify-center text-white"
                  style={{
                    width: 18,
                    height: 18,
                    backgroundColor: hue.solid,
                    fontFamily: POPPINS,
                    fontWeight: 800,
                    fontSize: 12,
                    marginTop: 1,
                  }}
                >
                  ✓
                </span>
                <span
                  style={{
                    fontFamily: POPPINS,
                    fontWeight: 400,
                    fontSize: 14.5,
                    color: '#374151',
                    lineHeight: 1.5,
                  }}
                >
                  {c}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Dashed connector — drawn between steps using a row of dots
const DashedDots = () => (
  <div className="flex items-center justify-center" style={{ height: 80 }}>
    <div className="flex items-center gap-2.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className="rounded-full"
          style={{ width: 7, height: 7, backgroundColor: '#9CA3AF' }}
        />
      ))}
    </div>
  </div>
);

const SectionTitle = ({ icon: Icon, children, color = '#1F2937' }) => (
  <div className="flex items-center gap-2 mb-3">
    {Icon && <Icon style={{ width: 16, height: 16, color }} strokeWidth={2.25} />}
    <span
      style={{
        fontFamily: POPPINS,
        fontWeight: 800,
        fontSize: 12,
        color,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </span>
  </div>
);

export default function IntelligenceReportModal({ open, onOpenChange, infographic, scopeLabel }) {
  const reportRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!reportRef.current) return;
    setDownloading(true);
    const t = toast.loading('Rendering PNG…');
    try {
      const dataUrl = await toPng(reportRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#FFFFFF',
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `intelligence-report-${new Date().toISOString().split('T')[0]}.png`;
      a.click();
      toast.success('PNG downloaded', { id: t });
    } catch (e) {
      toast.error(e.message || 'Failed to export PNG', { id: t });
    } finally {
      setDownloading(false);
    }
  };

  if (!infographic) return null;

  const cols = infographic.brand_columns || [];
  const totals = infographic.totals || {};
  const themesData = (infographic.creative_themes || []).filter((t) => t && t.themes);
  const cohortsData = (infographic.cohorts || []).filter((c) => c && c.label);
  const landingData = (infographic.landing_pages || []).filter((p) => p && p.label);
  const budget = (infographic.strategy?.budget_allocation || []).filter((s) => s && s.label);
  const creative = (infographic.strategy?.creative_allocation || []).filter((s) => s && s.label);
  const showStrategy = budget.length > 0 || creative.length > 0;
  const validLeaderboard = (infographic.leaderboard || []).filter((b) => b.roas != null);
  const showLeaderboard = validLeaderboard.length > 1;
  const showExec = !!infographic.executive_conclusion;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[98vw] max-w-[1800px] h-[96vh] p-0 overflow-hidden bg-white"
        data-testid="intelligence-report-modal"
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white shrink-0">
          <div className="flex items-center gap-2.5">
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center"
              style={{ backgroundColor: STEP_HUES[0].solid }}
            >
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div style={{ fontFamily: POPPINS, fontWeight: 700, fontSize: 13, color: '#111827' }}>
              Intelligence Report
            </div>
            {scopeLabel && (
              <span
                style={{
                  fontFamily: POPPINS,
                  fontWeight: 500,
                  fontSize: 11,
                  color: '#6B7280',
                  marginLeft: 4,
                }}
              >
                · {scopeLabel}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="text-white h-8 px-3"
              style={{ backgroundColor: STEP_HUES[0].solid, fontFamily: POPPINS, fontWeight: 600 }}
              data-testid="report-download-png-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Exporting…
                </>
              ) : (
                <>
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  Download PNG
                </>
              )}
            </Button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="w-8 h-8 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Scrollable canvas */}
        <div className="flex-1 min-h-0 overflow-auto bg-slate-50">
          {/* This is what gets exported as PNG */}
          <div
            ref={reportRef}
            className="mx-auto"
            style={{
              width: '1600px',
              minWidth: '1600px',
              backgroundColor: '#FFFFFF',
              padding: '56px 72px 64px',
              fontFamily: POPPINS,
            }}
          >
            {/* Title block — single big word, with metadata on the right */}
            <div className="flex items-start justify-between gap-10" style={{ marginBottom: 28 }}>
              <div className="min-w-0">
                <div
                  style={{
                    fontFamily: POPPINS,
                    fontWeight: 800,
                    fontSize: 12,
                    letterSpacing: '0.32em',
                    textTransform: 'uppercase',
                    color: STEP_HUES[0].solid,
                    marginBottom: 14,
                  }}
                >
                  Creative Performance Report
                </div>
                <div
                  style={{
                    fontFamily: POPPINS,
                    fontSize: 88,
                    lineHeight: 0.95,
                    letterSpacing: '-0.035em',
                    color: '#111827',
                  }}
                >
                  <span style={{ fontWeight: 400 }}>LEARNING </span>
                  <span style={{ fontWeight: 900, color: STEP_HUES[0].solid }}>INTELLIGENCE.</span>
                </div>
                <div
                  style={{
                    fontFamily: POPPINS,
                    fontWeight: 500,
                    fontSize: 14,
                    color: '#374151',
                    marginTop: 16,
                    maxWidth: 920,
                    lineHeight: 1.55,
                  }}
                >
                  {infographic.auto_summary ? (
                    <>
                      <span style={{ fontWeight: 700, color: STEP_HUES[0].solid, marginRight: 6 }}>
                        Thesis:
                      </span>
                      {infographic.auto_summary}
                    </>
                  ) : (
                    <>
                      Generated <strong style={{ fontWeight: 700, color: '#111827' }}>strictly from learnings recorded</strong> against each creative.
                      Sections without supporting learnings are intentionally omitted.
                    </>
                  )}
                </div>
              </div>

              {/* Totals stack on the right */}
              <div className="flex items-center gap-6 shrink-0" style={{ marginTop: 14 }}>
                <div className="text-center">
                  <div
                    style={{
                      fontFamily: POPPINS,
                      fontWeight: 800,
                      fontSize: 44,
                      color: STEP_HUES[1].solid,
                      lineHeight: 1,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {totals.learnings_count ?? 0}
                  </div>
                  <div
                    style={{
                      fontFamily: POPPINS,
                      fontWeight: 700,
                      fontSize: 10,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: '#9CA3AF',
                      marginTop: 8,
                    }}
                  >
                    Learnings
                  </div>
                </div>
                <div style={{ width: 1, height: 56, backgroundColor: '#E5E7EB' }} />
                <div className="text-center">
                  <div
                    style={{
                      fontFamily: POPPINS,
                      fontWeight: 800,
                      fontSize: 44,
                      color: STEP_HUES[2].solid,
                      lineHeight: 1,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {cols.length}
                  </div>
                  <div
                    style={{
                      fontFamily: POPPINS,
                      fontWeight: 700,
                      fontSize: 10,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: '#9CA3AF',
                      marginTop: 8,
                    }}
                  >
                    Brand{cols.length === 1 ? '' : 's'}
                  </div>
                </div>
                <div style={{ width: 1, height: 56, backgroundColor: '#E5E7EB' }} />
                <div className="text-center">
                  <div
                    style={{
                      fontFamily: POPPINS,
                      fontWeight: 800,
                      fontSize: 44,
                      color: '#374151',
                      lineHeight: 1,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {totals.creatives_count ?? 0}
                  </div>
                  <div
                    style={{
                      fontFamily: POPPINS,
                      fontWeight: 700,
                      fontSize: 10,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: '#9CA3AF',
                      marginTop: 8,
                    }}
                  >
                    Creatives
                  </div>
                </div>
              </div>
            </div>

            {/* Dotted divider */}
            <div
              style={{
                borderTop: '2px dotted #E5E7EB',
                marginTop: 4,
                marginBottom: 36,
              }}
            />

            {/* Brand chips strip — visible roll-call of brands + their learning weight */}
            {cols.length > 0 && (
              <div className="flex flex-wrap gap-2" style={{ marginBottom: 24 }}>
                {cols.map((c, i) => {
                  const hue = STEP_HUES[i % STEP_HUES.length];
                  return (
                    <span
                      key={i}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: hue.soft }}
                    >
                      <span
                        className="rounded-full"
                        style={{ width: 7, height: 7, backgroundColor: hue.solid }}
                      />
                      <span
                        style={{
                          fontFamily: POPPINS,
                          fontWeight: 700,
                          fontSize: 12,
                          color: hue.text,
                          letterSpacing: '0.02em',
                        }}
                      >
                        {c.brand}
                      </span>
                      <span
                        style={{
                          fontFamily: POPPINS,
                          fontWeight: 600,
                          fontSize: 10.5,
                          color: '#6B7280',
                          letterSpacing: '0.04em',
                        }}
                      >
                        · {c.learning_count ?? 0}L
                      </span>
                    </span>
                  );
                })}
              </div>
            )}

            {/* HORIZONTAL STEP FLOW — brands as numbered steps */}
            {cols.length > 0 && (
              <div
                className="grid"
                style={{
                  gridTemplateColumns: cols.flatMap((_, i) =>
                    i === 0 ? ['1fr'] : ['64px', '1fr']
                  ).join(' '),
                  alignItems: 'stretch',
                  gap: '0 18px',
                  marginBottom: 56,
                }}
              >
                {cols.map((c, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && (
                      <div className="flex items-center justify-center">
                        <DashedDots />
                      </div>
                    )}
                    <BrandStep col={c} idx={i} />
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Optional leaderboard row */}
            {showLeaderboard && (
              <div
                className="rounded-2xl border p-5"
                style={{ borderColor: '#E5E7EB', backgroundColor: '#FAFAFA', marginBottom: 40 }}
              >
                <SectionTitle icon={Trophy} color="#E0A310">
                  Overall Performance Leaderboard
                </SectionTitle>
                <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${validLeaderboard.length}, minmax(0,1fr))` }}>
                  {validLeaderboard.slice(0, 5).map((b, i) => {
                    const hue = STEP_HUES[i % STEP_HUES.length];
                    return (
                      <div
                        key={i}
                        className="rounded-lg px-3 py-2.5"
                        style={{ backgroundColor: hue.soft, borderLeft: `4px solid ${hue.solid}` }}
                      >
                        <div
                          style={{
                            fontFamily: POPPINS,
                            fontWeight: 700,
                            fontSize: 10,
                            color: '#6B7280',
                            letterSpacing: '0.12em',
                          }}
                        >
                          #{i + 1}
                        </div>
                        <div
                          style={{
                            fontFamily: POPPINS,
                            fontWeight: 800,
                            fontSize: 14,
                            color: hue.text,
                            marginTop: 2,
                          }}
                        >
                          {b.brand}
                        </div>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span
                            style={{
                              fontFamily: POPPINS,
                              fontWeight: 800,
                              fontSize: 16,
                              color: '#111827',
                            }}
                            className="tabular-nums"
                          >
                            {Number(b.roas).toFixed(2)}
                          </span>
                          <span
                            style={{
                              fontFamily: POPPINS,
                              fontWeight: 600,
                              fontSize: 10,
                              color: '#6B7280',
                              letterSpacing: '0.1em',
                            }}
                          >
                            ROAS · {b.learning_count ?? 0}L
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Supporting strips — only what learnings produced */}
            {(themesData.length > 0 || cohortsData.length > 0 || landingData.length > 0 || showStrategy) && (
              <div
                className="grid gap-5"
                style={{
                  gridTemplateColumns: `repeat(${Math.min(
                    (themesData.length > 0) +
                      (cohortsData.length > 0) +
                      (landingData.length > 0) +
                      (showStrategy ? 1 : 0),
                    4
                  )}, minmax(0, 1fr))`,
                  marginBottom: 40,
                }}
              >
                {themesData.length > 0 && (
                  <div className="rounded-2xl border p-4" style={{ borderColor: '#E5E7EB' }}>
                    <SectionTitle icon={Megaphone} color={STEP_HUES[0].solid}>
                      Creative Theme Performance
                    </SectionTitle>
                    <div className="space-y-2">
                      {themesData.map((t, i) => {
                        const hue = t.tier === 1 ? STEP_HUES[1] : t.tier === 2 ? STEP_HUES[3] : STEP_HUES[4];
                        return (
                          <div
                            key={i}
                            className="rounded-lg p-2.5"
                            style={{ backgroundColor: hue.soft }}
                          >
                            <div className="flex items-center justify-between">
                              <span
                                style={{
                                  fontFamily: POPPINS,
                                  fontWeight: 800,
                                  fontSize: 11,
                                  color: hue.text,
                                  letterSpacing: '0.04em',
                                }}
                              >
                                TIER {t.tier} · {t.label}
                              </span>
                              {t.avg_roas && (
                                <span
                                  style={{
                                    fontFamily: POPPINS,
                                    fontWeight: 700,
                                    fontSize: 11,
                                    color: '#111827',
                                  }}
                                  className="tabular-nums"
                                >
                                  {t.avg_roas}
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                fontFamily: POPPINS,
                                fontWeight: 400,
                                fontSize: 12,
                                color: '#374151',
                                marginTop: 4,
                                lineHeight: 1.45,
                              }}
                            >
                              {t.themes}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {cohortsData.length > 0 && (
                  <div className="rounded-2xl border p-4" style={{ borderColor: '#E5E7EB' }}>
                    <SectionTitle icon={Users} color={STEP_HUES[1].solid}>
                      Cohort Intelligence
                    </SectionTitle>
                    <div className="space-y-2">
                      {cohortsData.map((c, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <div
                              style={{
                                fontFamily: POPPINS,
                                fontWeight: 500,
                                fontSize: 12,
                                color: '#1F2937',
                              }}
                              className="truncate"
                            >
                              {c.label}
                            </div>
                          </div>
                          <div
                            className="rounded-full"
                            style={{
                              width: 80,
                              height: 6,
                              backgroundColor: '#E5E7EB',
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                position: 'absolute',
                                inset: 0,
                                width: `${((c.level || 1) / 5) * 100}%`,
                                backgroundColor: STEP_HUES[1].solid,
                              }}
                            />
                          </div>
                          {c.impact && (
                            <span
                              style={{
                                fontFamily: POPPINS,
                                fontWeight: 700,
                                fontSize: 9.5,
                                color: '#6B7280',
                                letterSpacing: '0.1em',
                                width: 90,
                                textAlign: 'right',
                              }}
                            >
                              {c.impact}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {landingData.length > 0 && (
                  <div className="rounded-2xl border p-4" style={{ borderColor: '#E5E7EB' }}>
                    <SectionTitle icon={Monitor} color={STEP_HUES[5].solid}>
                      Landing Page Insight
                    </SectionTitle>
                    <div className="space-y-2">
                      {landingData.map((p, i) => (
                        <div
                          key={i}
                          className="rounded-lg p-2.5"
                          style={{ backgroundColor: '#F3F4F6' }}
                        >
                          <div
                            style={{
                              fontFamily: POPPINS,
                              fontWeight: 700,
                              fontSize: 12,
                              color: '#111827',
                            }}
                          >
                            {p.label}
                          </div>
                          {p.verdict && (
                            <div
                              style={{
                                fontFamily: POPPINS,
                                fontWeight: 700,
                                fontSize: 10.5,
                                color: STEP_HUES[3].solid,
                                marginTop: 2,
                                letterSpacing: '0.04em',
                              }}
                            >
                              {p.verdict}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {showStrategy && (
                  <div className="rounded-2xl border p-4" style={{ borderColor: '#E5E7EB' }}>
                    <SectionTitle icon={Target} color={STEP_HUES[3].solid}>
                      Strategic Recommendations
                    </SectionTitle>
                    <div className="space-y-3">
                      {budget.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontFamily: POPPINS,
                              fontWeight: 700,
                              fontSize: 10,
                              color: '#6B7280',
                              letterSpacing: '0.12em',
                              textTransform: 'uppercase',
                              marginBottom: 4,
                            }}
                          >
                            Budget Allocation
                          </div>
                          {budget.map((s, i) => {
                            const hue = STEP_HUES[i % STEP_HUES.length];
                            return (
                              <div key={i} className="flex items-center gap-2 mb-1">
                                <span
                                  style={{
                                    fontFamily: POPPINS,
                                    fontWeight: 500,
                                    fontSize: 11.5,
                                    color: '#374151',
                                    width: 130,
                                  }}
                                  className="truncate"
                                >
                                  {s.label}
                                </span>
                                <div
                                  className="flex-1 rounded-full overflow-hidden"
                                  style={{ height: 6, backgroundColor: '#F3F4F6' }}
                                >
                                  <div
                                    style={{
                                      height: '100%',
                                      width: `${s.pct}%`,
                                      backgroundColor: hue.solid,
                                    }}
                                  />
                                </div>
                                <span
                                  style={{
                                    fontFamily: POPPINS,
                                    fontWeight: 700,
                                    fontSize: 11,
                                    color: hue.text,
                                    width: 32,
                                    textAlign: 'right',
                                  }}
                                  className="tabular-nums"
                                >
                                  {s.pct}%
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {creative.length > 0 && (
                        <div>
                          <div
                            style={{
                              fontFamily: POPPINS,
                              fontWeight: 700,
                              fontSize: 10,
                              color: '#6B7280',
                              letterSpacing: '0.12em',
                              textTransform: 'uppercase',
                              marginBottom: 4,
                            }}
                          >
                            Creative Allocation
                          </div>
                          {creative.map((s, i) => {
                            const hue = STEP_HUES[(i + 2) % STEP_HUES.length];
                            return (
                              <div key={i} className="flex items-center gap-2 mb-1">
                                <span
                                  style={{
                                    fontFamily: POPPINS,
                                    fontWeight: 500,
                                    fontSize: 11.5,
                                    color: '#374151',
                                    width: 130,
                                  }}
                                  className="truncate"
                                >
                                  {s.label}
                                </span>
                                <div
                                  className="flex-1 rounded-full overflow-hidden"
                                  style={{ height: 6, backgroundColor: '#F3F4F6' }}
                                >
                                  <div
                                    style={{
                                      height: '100%',
                                      width: `${s.pct}%`,
                                      backgroundColor: hue.solid,
                                    }}
                                  />
                                </div>
                                <span
                                  style={{
                                    fontFamily: POPPINS,
                                    fontWeight: 700,
                                    fontSize: 11,
                                    color: hue.text,
                                    width: 32,
                                    textAlign: 'right',
                                  }}
                                  className="tabular-nums"
                                >
                                  {s.pct}%
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recurring keywords — typographic word cloud (no pills, sized by frequency) */}
            {Array.isArray(infographic.keyword_cloud) && infographic.keyword_cloud.length > 0 && (() => {
              const kws = infographic.keyword_cloud;
              const maxCount = Math.max(...kws.map((k) => k.count || 1));
              const minCount = Math.min(...kws.map((k) => k.count || 1));
              // 5-tier scale -> font size; bigger range for dramatic word-cloud effect
              const tierOf = (count) => {
                if (maxCount === minCount) return 2;
                const r = (count - minCount) / (maxCount - minCount);
                if (r >= 0.88) return 4;   // hero
                if (r >= 0.66) return 3;
                if (r >= 0.40) return 2;
                if (r >= 0.18) return 1;
                return 0;
              };
              const FONT = { 0: 14, 1: 20, 2: 30, 3: 46, 4: 68 };
              const WEIGHT = { 0: 600, 1: 700, 2: 800, 3: 800, 4: 900 };
              // Color palette — accent (orange) + neutral greys, like the reference image
              const ACCENT = STEP_HUES[0].solid; // orange
              const NEUTRALS = ['#111827', '#1F2937', '#4B5563', '#6B7280', '#9CA3AF'];
              // Deterministic pseudo-random based on word, so PNG export stays stable
              const seedOf = (s) => {
                let h = 0;
                for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
                return h;
              };
              const colorFor = (word, tier) => {
                const seed = seedOf(word);
                // Hero tier ALWAYS accent. Tier 3 ~ 50% accent. Tier 2 ~ 25% accent. Otherwise neutral.
                const accentChance = { 4: 1, 3: 0.5, 2: 0.25, 1: 0.1, 0: 0 }[tier];
                if ((seed % 100) / 100 < accentChance) return ACCENT;
                return NEUTRALS[seed % NEUTRALS.length];
              };
              const caseFor = (word, tier) => {
                // Mix UPPERCASE / lowercase like the reference. Bigger words more likely uppercase.
                const seed = seedOf(word + 'c');
                const upperChance = { 4: 0.85, 3: 0.6, 2: 0.45, 1: 0.3, 0: 0.2 }[tier];
                return ((seed % 100) / 100 < upperChance) ? 'uppercase' : 'lowercase';
              };
              // Sort to put a few big ones near top-center visually (descending count first, then alphabetical)
              const sorted = [...kws].sort((a, b) => (b.count || 0) - (a.count || 0));
              // Re-shuffle deterministically so big words don't all clump on the left
              const interleaved = (() => {
                const out = [];
                const big = sorted.filter((k) => tierOf(k.count || 1) >= 3);
                const rest = sorted.filter((k) => tierOf(k.count || 1) < 3);
                // Insert big words at spaced positions across the rest
                const total = big.length + rest.length;
                if (total === 0) return sorted;
                const positions = big.map((_, i) =>
                  Math.floor(((i + 0.5) / big.length) * total)
                );
                let bi = 0;
                let ri = 0;
                for (let i = 0; i < total; i++) {
                  if (positions.includes(i) && bi < big.length) {
                    out.push(big[bi++]);
                  } else if (ri < rest.length) {
                    out.push(rest[ri++]);
                  } else if (bi < big.length) {
                    out.push(big[bi++]);
                  }
                }
                return out;
              })();

              return (
                <div style={{ marginBottom: 40 }}>
                  <div className="flex items-baseline justify-between" style={{ marginBottom: 16 }}>
                    <SectionTitle icon={Sparkles} color={ACCENT}>
                      Recurring in Learnings
                    </SectionTitle>
                    <div
                      style={{
                        fontFamily: POPPINS,
                        fontWeight: 600,
                        fontSize: 11,
                        color: '#9CA3AF',
                        letterSpacing: '0.04em',
                      }}
                    >
                      <span className="tabular-nums" style={{ color: '#374151', fontWeight: 800 }}>
                        {kws.length}
                      </span>{' '}
                      recurring patterns · sized by frequency
                    </div>
                  </div>

                  <div
                    className="flex flex-wrap items-baseline justify-center"
                    style={{
                      padding: '40px 48px',
                      borderRadius: 18,
                      background:
                        'radial-gradient(ellipse at center, #FFFFFF 0%, #FAFAFA 70%, #F4F4F5 100%)',
                      border: '1px solid #E5E7EB',
                      columnGap: 18,
                      rowGap: 4,
                    }}
                  >
                    {interleaved.map((k) => {
                      const tier = tierOf(k.count || 1);
                      const isHero = tier === 4;
                      return (
                        <span
                          key={k.word}
                          title={`${k.word} · ${k.count}`}
                          style={{
                            fontFamily: POPPINS,
                            fontWeight: WEIGHT[tier],
                            fontSize: FONT[tier],
                            color: colorFor(k.word, tier),
                            textTransform: caseFor(k.word, tier),
                            letterSpacing: tier >= 3 ? '-0.025em' : '-0.005em',
                            lineHeight: 1.05,
                            display: 'inline-block',
                            whiteSpace: 'nowrap',
                            // Subtle vertical jitter for organic feel — deterministic per word
                            transform: `translateY(${((seedOf(k.word) % 7) - 3)}px)`,
                            textShadow: isHero ? `0 1px 0 ${ACCENT}10` : 'none',
                          }}
                        >
                          {k.word}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Executive conclusion ribbon */}
            {showExec && (
              <div
                className="rounded-2xl flex items-start gap-4"
                style={{
                  backgroundColor: STEP_HUES[0].soft,
                  border: `1px solid ${STEP_HUES[0].solid}33`,
                  padding: '22px 26px',
                }}
              >
                <div
                  className="shrink-0 rounded-full flex items-center justify-center"
                  style={{ width: 44, height: 44, backgroundColor: STEP_HUES[0].solid }}
                >
                  <Trophy className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    style={{
                      fontFamily: POPPINS,
                      fontWeight: 800,
                      fontSize: 11,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: STEP_HUES[0].text,
                    }}
                  >
                    Executive Conclusion
                  </div>
                  <div
                    style={{
                      fontFamily: POPPINS,
                      fontWeight: 500,
                      fontSize: 15,
                      color: '#1F2937',
                      marginTop: 8,
                      lineHeight: 1.55,
                    }}
                  >
                    {infographic.executive_conclusion}
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div
              className="flex items-center justify-between"
              style={{
                marginTop: 36,
                paddingTop: 18,
                borderTop: '2px dotted #E5E7EB',
              }}
            >
              <span
                style={{
                  fontFamily: POPPINS,
                  fontWeight: 500,
                  fontSize: 11,
                  color: '#9CA3AF',
                  letterSpacing: '0.04em',
                }}
              >
                Generated from learnings · {scopeLabel || 'All creatives'}
              </span>
              <span
                style={{
                  fontFamily: POPPINS,
                  fontWeight: 700,
                  fontSize: 11,
                  color: '#9CA3AF',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                }}
              >
                PRISM Intelligence
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
